// Step progress indicator for the property wizard.
//
// Desktop: full step-rail with circle markers + Vietnamese labels under
// each step, click to jump. Matches design comp's `<button>` row with
// the active step underlined in accent.
//
// Mobile: four thin progress bars side-by-side. Past / current / future
// states use ok / accent / border colors.

import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  WIZARD_STEPS,
  stepIndex,
  type WizardStep,
} from "@/features/property/wizard/wizard-state";

interface StepProgressProps {
  current: WizardStep;
  /** Optional: enables tap-to-jump on both rails when supplied. */
  onJump?: (step: WizardStep) => void;
}

export function StepProgress({ current, onJump }: StepProgressProps) {
  return (
    <>
      <DesktopRail current={current} {...(onJump !== undefined ? { onJump } : {})} />
      <MobileBars current={current} {...(onJump !== undefined ? { onJump } : {})} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Desktop
// ---------------------------------------------------------------------------

function DesktopRail({
  current,
  onJump,
}: {
  current: WizardStep;
  onJump?: (step: WizardStep) => void;
}) {
  const currentIdx = stepIndex(current);
  return (
    <div className="hidden border-b border-border bg-card md:flex md:px-8">
      {WIZARD_STEPS.map((s, i) => {
        const isCurrent = s.key === current;
        const isDone = i < currentIdx;
        const canJump = onJump !== undefined;
        return (
          <button
            key={s.key}
            type="button"
            onClick={canJump ? () => onJump?.(s.key) : undefined}
            aria-current={isCurrent ? "step" : undefined}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 py-3.5 text-[12px] transition-colors",
              "border-b-2 border-transparent",
              isCurrent && "border-primary font-semibold text-primary",
              isDone && !isCurrent && "text-ink-soft",
              !isCurrent && !isDone && "text-muted-foreground",
              canJump && !isCurrent && "hover:text-foreground",
            )}
          >
            <span
              className={cn(
                "flex size-5 items-center justify-center rounded-full font-mono text-[11px] font-bold",
                isDone && "bg-success text-background",
                isCurrent && "bg-primary text-primary-foreground",
                !isDone && !isCurrent && "bg-muted text-ink-faint",
              )}
            >
              {isDone ? <Check aria-hidden="true" className="size-2.5" /> : i + 1}
            </span>
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile — 4 thin bars
// ---------------------------------------------------------------------------

function MobileBars({
  current,
  onJump,
}: {
  current: WizardStep;
  onJump?: (step: WizardStep) => void;
}) {
  const currentIdx = stepIndex(current);
  return (
    <div
      role="tablist"
      aria-label="Bước"
      className="flex gap-1 px-4 md:hidden"
    >
      {WIZARD_STEPS.map((s, i) => {
        const isCurrent = s.key === current;
        const isDone = i < currentIdx;
        const canJump = onJump !== undefined;
        return (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={isCurrent}
            aria-label={`${s.label} (bước ${i + 1})`}
            onClick={canJump ? () => onJump?.(s.key) : undefined}
            className="group flex flex-1 items-center justify-center py-2.5 focus-visible:outline-none"
          >
            <span
              aria-hidden="true"
              className={cn(
                "h-1 w-full rounded-full transition-colors",
                "group-focus-visible:ring-2 group-focus-visible:ring-ring",
                isDone && "bg-success",
                isCurrent && "bg-primary",
                !isDone && !isCurrent && "bg-border",
              )}
            />
          </button>
        );
      })}
    </div>
  );
}
